import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import Layout from "../components/Layout";
import AuthImage from "../components/AuthImage";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Search, CheckCircle2, AlertTriangle, ShieldAlert, X, Bell, Plus, Loader2 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

async function openReverseTabs(imageId) {
  // Hent 5-min signert URL og åpne 2 motorer som fungerer med signerte URLer.
  // Andre motorer (Sogou, Baidu, Copyseeker, TinEye, PimEyes, Bing) fungerer IKKE
  // med tokenizede/query-URLer — dokumentert i /app/memory/leak_check_excluded_engines.md.
  // Enterprise DMCA-integrasjon (P2) løser scanning-behovet skikkelig.
  const r = await api.get(`/media/sign`, { params: { kind: "image", id: imageId, ttl: 300 } });
  const fullUrl = `${BACKEND}${r.data.url}`;
  const enc = encodeURIComponent(fullUrl);
  const urls = [
    `https://lens.google.com/uploadbyurl?url=${enc}`,
    `https://yandex.com/images/search?url=${enc}&rpt=imageview`,
  ];
  urls.forEach((u, i) => setTimeout(() => window.open(u, "_blank", "noopener,noreferrer"), i * 400));
}

function ResultBadge({ result }) {
  if (result === "ok") return <span className="text-emerald-400 text-xs">✓ Ingen treff</span>;
  if (result === "suspicious") return <span className="text-amber-400 text-xs">⚠ Mistenkelig</span>;
  if (result === "leak") return <span className="text-red-400 text-xs">🚨 Lekkasje</span>;
  return <span className="text-white/30 text-xs">Ikke sjekket</span>;
}

export default function LeakSjekk() {
  const { user } = useAuth();
  const [refs, setRefs] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [settings, setSettings] = useState(null);
  const [lastFullScan, setLastFullScan] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [dmcaImageId, setDmcaImageId] = useState(null);
  const [showManualDmca, setShowManualDmca] = useState(false);

  const openLeakUpload = () => {
    setShowAdd(false);
    window.location.href = "/leak-sjekk/opplasting";
  };

  const load = async () => {
    // Auto-marker nye bilder i leak-albumet som referanser
    try { await api.patch("/leak-check/references/auto-mark"); } catch { /* ignore */ }
    const [rRefs, rSettings] = await Promise.all([
      api.get("/leak-check/references"),
      api.get("/leak-check/settings"),
    ]);
    setRefs(rRefs.data.references || []);
    setSettings(rSettings.data.settings);
    setLastFullScan(rSettings.data.last_full_scan_at);
  };

  useEffect(() => { load(); }, []);

  const loadCandidates = async () => {
    const r = await api.get("/leak-check/candidates", { params: { limit: 100 } });
    setCandidates(r.data.candidates || []);
  };

  const addToLibrary = async (id) => {
    await api.post(`/leak-check/references/${id}`);
    setCandidates((c) => c.filter((x) => x.id !== id));
    load();
  };

  const removeFromLibrary = async (id) => {
    if (!window.confirm("Fjerne fra referanse-biblioteket?")) return;
    await api.delete(`/leak-check/references/${id}`);
    load();
  };

  const scanAll = async () => {
    if (!refs.length) { toast.error("Ingen referanse-bilder å skanne."); return; }
    const proceed = window.confirm(
      `Dette åpner ${refs.length * 2} nye faner (2 motorer × ${refs.length} bilder).\n\n` +
      `VIKTIG: Etter skanning må du selv se gjennom hver fane og markere resultat på hvert bilde:\n` +
      `  ✓  Ingen treff\n` +
      `  ⚠  Mistenkelig\n` +
      `  🚨 Lekkasje funnet (åpner DMCA-brev-generator)\n\n` +
      `Bilder du ikke markerer forblir «Ikke sjekket».\n\n` +
      `Tillat pop-ups hvis nettleseren spør. Fortsette?`
    );
    if (!proceed) return;
    setScanning(true);
    try {
      for (const r of refs) {
         
        await openReverseTabs(r.id);
         
        await new Promise((res) => setTimeout(res, 2500));
      }
      await api.post("/leak-check/scan-batch");
      toast.success("Faner åpnet. Bla gjennom og marker hvert bilde med ✓/⚠/🚨.");
      load();
    } catch (e) {
      toast.error("Feil under skanning: " + (e.response?.data?.detail || e.message));
    } finally {
      setScanning(false);
    }
  };

  const markResult = async (id, result) => {
    if (result === "leak") {
      setDmcaImageId(id);
      return;
    }
    const note = result === "suspicious" ? window.prompt("Notat (URL eller detaljer, valgfritt):") : null;
    await api.post(`/leak-check/references/${id}/mark`, { result, note });
    load();
  };

  const saveSettings = async (s) => {
    try {
      const r = await api.patch("/leak-check/settings", s);
      setSettings(r.data.settings);
      toast.success("Innstillinger lagret");
      setShowSettings(false);
    } catch (e) {
      toast.error("Kunne ikke lagre: " + (e.response?.data?.detail || e.message));
    }
  };

  if (!user?.is_creator) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto text-center py-20">
          <h1 className="font-fraunces italic text-3xl text-[var(--cream)] mb-3">Leak-sjekk (for skapere)</h1>
          <p className="text-[var(--cream-soft)] mb-4">
            Automatisk skanning og overvåkning av dine referanse-bilder er tilgjengelig for verifiserte skapere.
          </p>
          <div className="mb-6">
            <Link to="/bli-onlyfans-skaper" className="btn-primary">Bli skaper</Link>
          </div>

          <div className="mt-10 pt-8 border-t border-white/10">
            <h2 className="font-fraunces italic text-xl text-[var(--cream)] mb-2">Har du funnet en lekkasje?</h2>
            <p className="text-[var(--cream-soft)] text-sm mb-4 max-w-md mx-auto">
              Generer et ferdig DMCA-brev du kan sende til leak-nettstedet — helt gratis og åpent for alle.
            </p>
            <button
              onClick={() => window.open("/dmca", "_self")}
              className="btn-primary"
              data-testid="dmca-manual-open-fan"
            >
              🚨 Åpne DMCA-brev-generator
            </button>
          </div>
        </div>

        {showManualDmca && (
          <DMCAModal
            manual
            userEmail={user?.email || ""}
            userName={user?.username || ""}
            onClose={() => setShowManualDmca(false)}
            onDone={() => setShowManualDmca(false)}
          />
        )}
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-3 mb-2">
          <ShieldAlert className="h-7 w-7 text-[var(--magenta)]" />
          <h1 className="font-fraunces italic text-4xl text-[var(--cream)]">Leak-sjekk</h1>
        </div>
        <p className="text-[var(--cream-soft)] text-sm mb-6 max-w-2xl">
          Marker dine egne bilder som «referanse», og bruk master-knappen for å sjekke om de er lekket
          på Google Lens og Yandex.
        </p>

        <div className="mb-6">
          <Link
            to="/dmca"
            className="inline-block text-xs uppercase tracking-widest border border-[var(--magenta)] text-[var(--magenta)] px-4 py-2 hover:bg-[var(--magenta)]/10"
            data-testid="dmca-manual-open"
          >
            🚨 Manuell DMCA — for lekkasjer du finner utenom skanning
          </Link>
        </div>

        {/* Status-boks */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
          <div className="panel-tight p-4 border border-white/10">
            <div className="text-[10px] uppercase tracking-widest text-[var(--cream-soft)]">Referanse-bilder</div>
            <div className="font-fraunces italic text-3xl text-[var(--cream)] mt-1">{refs.length}</div>
          </div>
          <div className="panel-tight p-4 border border-white/10">
            <div className="text-[10px] uppercase tracking-widest text-[var(--cream-soft)]">Sist full skanning</div>
            <div className="font-fraunces italic text-xl text-[var(--cream)] mt-1">
              {lastFullScan ? new Date(lastFullScan).toLocaleString("nb-NO", { dateStyle: "short", timeStyle: "short" }) : "—"}
            </div>
          </div>
          <div className="panel-tight p-4 border border-white/10">
            <div className="text-[10px] uppercase tracking-widest text-[var(--cream-soft)]">Påminnelse</div>
            <div className="font-fraunces italic text-xl text-[var(--cream)] mt-1">
              {settings?.frequency === "off" ? "Av" : settings?.frequency === "weekly" ? "Ukentlig" : "Månedlig"}
            </div>
          </div>
        </div>

        {/* Handling-knapper */}
        <div className="flex flex-wrap gap-2 mb-6">
          <button
            onClick={scanAll}
            disabled={scanning || refs.length === 0}
            className="btn-primary inline-flex items-center gap-2 disabled:opacity-40"
            data-testid="scan-all-btn"
          >
            {scanning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            Sjekk alle nå ({refs.length * 2} faner)
          </button>
          <button
            onClick={() => { setShowAdd(true); loadCandidates(); }}
            className="btn-ghost inline-flex items-center gap-2"
            data-testid="add-references-btn"
          >
            <Plus className="h-4 w-4" /> Legg til bilder
          </button>
          <button
            onClick={() => setShowSettings(true)}
            className="btn-ghost inline-flex items-center gap-2"
            data-testid="settings-btn"
          >
            <Bell className="h-4 w-4" /> Innstillinger
          </button>
        </div>

        {/* Referanse-grid */}
        {refs.length === 0 ? (
          <div className="editorial-card p-10 text-center border border-white/10">
            <p className="text-[var(--cream-soft)] mb-4">Du har ikke lagt til noen referanse-bilder enda.</p>
            <button onClick={() => { setShowAdd(true); loadCandidates(); }} className="btn-primary">
              Kom i gang — velg bilder
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {refs.map((r) => (
              <div key={r.id} className="relative panel-tight border border-white/10 overflow-hidden" data-testid={`ref-${r.id}`}>
                <div className="aspect-square">
                  <AuthImage imageId={r.id} className="w-full h-full object-cover" />
                </div>
                <button
                  onClick={() => removeFromLibrary(r.id)}
                  className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/70 hover:bg-red-500 text-white flex items-center justify-center"
                  title="Fjern fra bibliotek"
                >
                  <X className="h-3 w-3" />
                </button>
                <div className="p-2 text-[10px]">
                  <ResultBadge result={r.leak_last_result} />
                  <div className="text-white/40 mt-0.5">
                    {r.leak_last_scanned_at ? new Date(r.leak_last_scanned_at).toLocaleDateString("nb-NO") : "Aldri sjekket"}
                  </div>
                  <div className="flex gap-1 mt-2">
                    <button onClick={() => openReverseTabs(r.id)} className="flex-1 py-1 bg-[var(--magenta)]/20 hover:bg-[var(--magenta)] text-[10px]" title="Sjekk kun dette bildet">
                      <Search className="h-3 w-3 inline" />
                    </button>
                    <button onClick={() => markResult(r.id, "ok")} className="flex-1 py-1 bg-emerald-500/20 hover:bg-emerald-500 text-[10px]" title="Ingen treff">
                      <CheckCircle2 className="h-3 w-3 inline" />
                    </button>
                    <button onClick={() => markResult(r.id, "suspicious")} className="flex-1 py-1 bg-amber-500/20 hover:bg-amber-500 text-[10px]" title="Mistenkelig">
                      <AlertTriangle className="h-3 w-3 inline" />
                    </button>
                    <button onClick={() => markResult(r.id, "leak")} className="flex-1 py-1 bg-red-500/20 hover:bg-red-500 text-[10px]" title="Lekkasje funnet">
                      <ShieldAlert className="h-3 w-3 inline" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Legg til-modal */}
        {showAdd && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setShowAdd(false)}>
            <div className="editorial-card p-6 max-w-4xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-fraunces italic text-2xl text-[var(--cream)]">Velg bilder til referanse-bibliotek</h2>
                <button onClick={() => setShowAdd(false)}><X className="h-5 w-5" /></button>
              </div>
              <div className="editorial-card p-4 mb-4 border border-[var(--magenta)]/30 bg-[var(--magenta)]/5">
                <div className="font-fraunces italic text-lg text-[var(--cream)] mb-2">🎯 Hvilke bilder gir best resultat?</div>
                <ul className="text-xs text-[var(--cream-soft)] space-y-1 leading-relaxed">
                  <li>• <b>1-3 ansiktsbilder</b> — Yandex finner bilder av deg selv med beskjæring/vannmerke</li>
                  <li>• <b>10-15 signatur-bilder</b> — unike detaljer: tatoveringer, sett/klær, karakteristiske positurer</li>
                  <li>• <b>Screenshots fra videoer</b> — 2-3 still-frames per video for å fange video-leaks</li>
                  <li>• <b>Unngå:</b> generiske selfies uten unike detaljer, gruppebilder</li>
                  <li>• <b>Ikke last opp «alt»</b> — 10-30 bilder er ideelt</li>
                </ul>
                <div className="mt-3 pt-3 border-t border-white/10 text-xs text-[var(--cream-soft)]">
                  Har du ikke bildene på plattformen enda?{" "}
                  <button
                    onClick={openLeakUpload}
                    className="text-[var(--magenta)] underline hover:text-[var(--cream)]"
                    data-testid="upload-new-leak-images"
                  >
                    Last opp til privat leak-album →
                  </button>
                  <div className="mt-1 text-[10px] text-[var(--cream-faint)]">
                    Albumet er knyttet til skaperprofilen din og automatisk satt til privat. Kun du kan se det. Nye bilder legges automatisk til referanse-biblioteket.
                  </div>
                </div>
              </div>
              {candidates.length === 0 ? (
                <p className="text-[var(--cream-soft)] text-sm">Ingen tilgjengelige bilder på plattformen enda. Bruk «Last opp» over.</p>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                  {candidates.map((c) => (
                    <button key={c.id} onClick={() => addToLibrary(c.id)} className="aspect-square panel-tight border border-white/10 hover:border-[var(--magenta)] overflow-hidden relative group" data-testid={`add-${c.id}`}>
                      <AuthImage imageId={c.id} className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/60 flex items-center justify-center transition">
                        <Plus className="h-6 w-6 text-white opacity-0 group-hover:opacity-100" />
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Innstillinger-modal */}
        {showSettings && settings && (
          <SettingsModal
            initial={settings}
            onSave={saveSettings}
            onClose={() => setShowSettings(false)}
          />
        )}

        {/* DMCA-modal (fra skanning) */}
        {dmcaImageId && (
          <DMCAModal
            imageId={dmcaImageId}
            userEmail={user?.email || ""}
            userName={user?.username || ""}
            onClose={() => setDmcaImageId(null)}
            onDone={() => { setDmcaImageId(null); load(); }}
          />
        )}

        {/* DMCA-modal (manuell — når bruker finner leaks selv) */}
        {showManualDmca && (
          <DMCAModal
            manual
            userEmail={user?.email || ""}
            userName={user?.username || ""}
            onClose={() => setShowManualDmca(false)}
            onDone={() => setShowManualDmca(false)}
          />
        )}

        <p className="text-[10px] text-[var(--cream-faint)] mt-8 max-w-2xl">
          Skanningen åpner offentlige reverse-search-tjenester i nye faner. Du må selv bla gjennom resultatene og markere om du fant lekkasje. Vi lagrer aldri innhold fra disse tjenestene.
        </p>
      </div>
    </Layout>
  );
}

function SettingsModal({ initial, onSave, onClose }) {
  const [s, setS] = useState(initial);
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="editorial-card p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-fraunces italic text-2xl text-[var(--cream)]">Påminnelses-innstillinger</h2>
          <button onClick={onClose}><X className="h-5 w-5" /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-xs uppercase tracking-widest text-[var(--cream-soft)] mb-1">Frekvens</label>
            <select value={s.frequency} onChange={(e) => setS({ ...s, frequency: e.target.value })} className="input-editorial w-full" data-testid="freq-select">
              <option value="off">Av (ingen påminnelser)</option>
              <option value="weekly">Ukentlig</option>
              <option value="monthly">Månedlig</option>
            </select>
          </div>
          {s.frequency !== "off" && (
            <>
              <div>
                <label className="block text-xs uppercase tracking-widest text-[var(--cream-soft)] mb-1">Varsel-metoder</label>
                <label className="flex items-center gap-2 text-sm mb-1">
                  <input type="checkbox" checked={s.notify_email} onChange={(e) => setS({ ...s, notify_email: e.target.checked })} data-testid="notify-email" />
                  E-post
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={s.notify_onsite} onChange={(e) => setS({ ...s, notify_onsite: e.target.checked })} data-testid="notify-onsite" />
                  Varsel på siden
                </label>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-[var(--cream-soft)] mb-1">Varsel-tid før neste sjekk</label>
                <select value={s.remind_hours_before} onChange={(e) => setS({ ...s, remind_hours_before: Number(e.target.value) })} className="input-editorial w-full">
                  <option value={1}>1 time</option>
                  <option value={6}>6 timer</option>
                  <option value={12}>12 timer</option>
                  <option value={24}>24 timer</option>
                  <option value={48}>2 dager</option>
                </select>
              </div>
            </>
          )}
          <button onClick={() => onSave(s)} className="btn-primary w-full" data-testid="save-settings">Lagre</button>
        </div>
      </div>
    </div>
  );
}

function DMCAModal({ imageId, userEmail, userName, manual = false, onClose, onDone }) {
  const [form, setForm] = useState({
    infringing_url: "",
    infringing_domain: "",
    contact_email: userEmail || "",
    contact_phone: "",
    full_name: "", // MÅ fylles ut manuelt — ekte juridisk navn kreves for DMCA
    original_url: "",
    additional_notes: "",
  });
  const [letter, setLetter] = useState(null);
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    if (!form.infringing_url || !form.contact_email || !form.full_name.trim()) {
      toast.error("Navn, URL og e-post er påkrevd");
      return;
    }
    setBusy(true);
    try {
      if (manual) {
        const r = await api.post(`/leak-check/dmca-manual`, form);
        setLetter(r.data);
      } else {
        const r = await api.post(`/leak-check/references/${imageId}/dmca`, form);
        setLetter(r.data);
        // Marker som lekkasje samtidig
        await api.post(`/leak-check/references/${imageId}/mark`, {
          result: "leak",
          note: form.infringing_url,
        });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Feil");
    } finally {
      setBusy(false);
    }
  };

  const download = () => {
    const blob = new Blob([letter.letter], { type: "text/plain" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = letter.filename;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const copy = async () => {
    await navigator.clipboard.writeText(letter.letter);
    toast.success("Kopiert til utklippstavle");
  };

  // Strip protokoll og trailing slash slik at abuse@ + domenet blir korrekt
  const cleanDomain = (d) => (d || "")
    .trim()
    .replace(/^https?:\/\//i, "")
    .replace(/\/.*$/, "")
    .replace(/^www\./i, "");

  return (
    <div className="fixed inset-0 bg-black/80 z-50 overflow-y-auto py-6 px-4" onClick={onClose}>
      <div className="editorial-card p-6 max-w-2xl w-full mx-auto my-auto min-h-fit" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-fraunces italic text-2xl text-[var(--cream)]">🚨 DMCA-takedown</h2>
          <button onClick={onClose}><X className="h-5 w-5" /></button>
        </div>

        {!letter ? (
          <div className="space-y-3">
            <p className="text-xs text-[var(--cream-soft)]">
              Fyll ut skjema, så genererer vi et ferdig DMCA-brev du kan sende til leak-nettstedet eller deres hosting-leverandør.
            </p>
            <div>
              <label className="text-xs text-[var(--cream-soft)]">URL til lekkasje *</label>
              <input value={form.infringing_url} onChange={(e) => setForm({ ...form, infringing_url: e.target.value })} placeholder="https://leak-site.com/…" className="input-editorial w-full" data-testid="dmca-url" />
            </div>
            <div>
              <label className="text-xs text-[var(--cream-soft)]">Domene (valgfritt)</label>
              <input value={form.infringing_domain} onChange={(e) => setForm({ ...form, infringing_domain: e.target.value })} placeholder="leak-site.com" className="input-editorial w-full" />
            </div>
            <div>
              <label className="text-xs text-[var(--cream-soft)]">
                URL til originalt innhold (valgfritt)
              </label>
              <input
                value={form.original_url}
                onChange={(e) => setForm({ ...form, original_url: e.target.value })}
                placeholder="https://onlyfans.com/dittbrukernavn eller https://norskerotikk.no/profil/dittbrukernavn"
                className="input-editorial w-full"
                data-testid="dmca-original-url"
              />
              <div className="text-[10px] text-[var(--cream-faint)] mt-1">
                Peker til der originalen er publisert lovlig — profil, OnlyFans, Fansly osv.
                Hvis blank, står det at originalen er lagret privat hos NorskErotikk.no.
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[var(--cream-soft)]">Ditt fulle navn *</label>
                <input
                  value={form.full_name}
                  onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                  placeholder="Ola Nordmann"
                  className="input-editorial w-full"
                  data-testid="dmca-name"
                />
                <div className="text-[10px] text-[var(--cream-faint)] mt-1">
                  Må være ekte juridisk navn — brukernavn er ikke gyldig for DMCA.
                </div>
              </div>
              <div>
                <label className="text-xs text-[var(--cream-soft)]">Din e-post *</label>
                <input value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className="input-editorial w-full" />
              </div>
            </div>
            <div>
              <label className="text-xs text-[var(--cream-soft)]">Telefon (valgfritt)</label>
              <input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} placeholder="+47 9XX XX XXX" className="input-editorial w-full" />
            </div>
            <div>
              <label className="text-xs text-[var(--cream-soft)]">Tilleggsnotat (valgfritt)</label>
              <textarea
                value={form.additional_notes}
                onChange={(e) => setForm({ ...form, additional_notes: e.target.value })}
                rows={3}
                placeholder="Ekstra kontekst til hosten – f.eks. flere URLer med samme bilde, eller referanse til tidligere ubesvarte varsler."
                className="input-editorial w-full"
                data-testid="dmca-notes"
              />
              <div className="text-[10px] text-[var(--cream-faint)] mt-1">
                Brevet inneholder allerede standard good-faith- og perjury-erklæring på engelsk.
                Bruk dette feltet kun til ekstra kontekst.
              </div>
            </div>
            <button onClick={generate} disabled={busy} className="btn-primary w-full" data-testid="generate-dmca">
              {busy ? "Genererer…" : "Generer DMCA-brev"}
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex gap-2">
              <button onClick={download} className="btn-primary flex-1" data-testid="download-dmca">📄 Last ned .txt</button>
              <button onClick={copy} className="btn-ghost flex-1" data-testid="copy-dmca">📋 Kopiér tekst</button>
            </div>
            <pre className="bg-black/40 p-3 text-[10px] text-[var(--cream-soft)] whitespace-pre-wrap font-mono max-h-96 overflow-y-auto border border-white/10">
              {letter.letter}
            </pre>
            <div className="text-xs text-[var(--cream-soft)] leading-relaxed border-t border-white/10 pt-4">
              <b className="text-[var(--cream)]">Hvor sender du brevet?</b>
              <p className="mt-1.5 text-[11px] text-[var(--cream-faint)]">
                Send til alle tre parallelt for maksimal effekt. Hosting-leverandøren og Google
                svarer typisk raskere enn selve leak-nettstedet.
              </p>
              <ol className="mt-2 space-y-2 list-decimal list-inside">
                <li>
                  <b>DMCA-agent på leak-nettstedet</b>
                  <div className="text-[11px] text-[var(--cream-faint)] mt-0.5 ml-4">
                    <div className="mb-1.5">Slik finner du kontakt:</div>
                    <ul className="space-y-0.5">
                      <li>• Se etter lenke i footeren: <b className="text-[var(--cream-soft)]">Contact</b>, <b className="text-[var(--cream-soft)]">Legal</b>, <b className="text-[var(--cream-soft)]">DMCA</b>, <b className="text-[var(--cream-soft)]">Copyright</b>, <b className="text-[var(--cream-soft)]">Report</b> eller <b className="text-[var(--cream-soft)]">Abuse</b></li>
                      <li>• Sjekk om siden har et <b className="text-[var(--cream-soft)]">kontaktskjema</b> — det er ofte deres eneste kanal</li>
                      <li>• Se etter en <b className="text-[var(--cream-soft)]">DMCA-agent</b> eksplisitt oppgitt (påkrevd hvis siden er hostet i USA)</li>
                    </ul>
                  </div>
                </li>
                <li>
                  <b>Hosting-leverandøren</b> (som drifter serveren deres)
                  <div className="text-[11px] text-[var(--cream-faint)] mt-0.5 ml-4">
                    Slå opp domenet på{" "}
                    <a
                      href={cleanDomain(form.infringing_domain) ? `https://who.is/whois/${cleanDomain(form.infringing_domain)}` : "https://who.is"}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[var(--magenta)] underline"
                    >
                      who.is
                    </a>{" "}
                    for å se hosting-selskapet, deretter{" "}
                    <a
                      href={cleanDomain(form.infringing_domain) ? `https://hostingchecker.com/${cleanDomain(form.infringing_domain)}` : "https://hostingchecker.com"}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[var(--magenta)] underline"
                    >
                      hostingchecker.com
                    </a>{" "}
                    for å bekrefte hvem som fysisk hoster serveren. Vanlige abuse-adresser:
                    <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 mt-1 text-[10px] font-mono">
                      <div>Cloudflare</div><div>abuse@cloudflare.com</div>
                      <div>Hetzner</div><div>abuse@hetzner.com</div>
                      <div>OVH</div><div>abuse@ovh.net</div>
                      <div>DigitalOcean</div><div>abuse@digitalocean.com</div>
                      <div>AWS</div><div>abuse@amazonaws.com</div>
                      <div>Namecheap</div><div>abuse@namecheap.com</div>
                    </div>
                  </div>
                </li>
                <li>
                  <b>Google Search</b> — for å fjerne fra søkeresultater
                  <div className="text-[11px] text-[var(--cream-faint)] mt-0.5 ml-4">
                    Fyll ut skjema hos{" "}
                    <a
                      href="https://reportcontent.google.com/forms/dmca_search"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[var(--magenta)] underline"
                    >
                      reportcontent.google.com
                    </a>. Selv om siden ikke fjerner innholdet, forsvinner den fra Google-søk innen 24-72 timer.
                  </div>
                </li>
              </ol>

              <div className="mt-4 border-t border-white/10 pt-3">
                <b className="text-[var(--cream)]">Hvis ingenting funker</b>
                <p className="mt-1 text-[11px] text-[var(--cream-faint)]">
                  Enkelte lek-sider ignorerer DMCA-brev. Da har du fortsatt disse alternativene:
                </p>
                <ul className="mt-1.5 space-y-1.5 text-[11px] text-[var(--cream-faint)]">
                  <li>
                    • <b className="text-[var(--cream-soft)]">Domene-registratoren</b> — WHOIS viser hvem som selger domenet (feks. GoDaddy, Namecheap). De kan i verste fall <i>suspendere hele domenet</i> hvis siden bryter deres ToS. Send DMCA til deres abuse-e-post.
                  </li>
                  <li>
                    • <b className="text-[var(--cream-soft)]">Betalings-prosessorer</b> — hvis siden tar betaling: rapporter til <a href="https://usa.visa.com/support/consumer/security.html" target="_blank" rel="noopener noreferrer" className="text-[var(--magenta)] underline">Visa</a>, <a href="https://www.mastercard.us/en-us/personal/get-support.html" target="_blank" rel="noopener noreferrer" className="text-[var(--magenta)] underline">Mastercard</a>, <a href="https://www.paypal.com/us/webapps/mpp/security/report-a-problem" target="_blank" rel="noopener noreferrer" className="text-[var(--magenta)] underline">PayPal</a>. Krav om chargeback fra flere brukere kan tvinge dem til å stenge butikken.
                  </li>
                  <li>
                    • <b className="text-[var(--cream-soft)]">Betalt takedown-tjeneste</b> — <a href="https://www.dmca.com/premium-services.aspx" target="_blank" rel="noopener noreferrer" className="text-[var(--magenta)] underline">DMCA.com</a> tar $199 for en enkelt-takedown de forfølger til slutten. <a href="https://rulta.com" target="_blank" rel="noopener noreferrer" className="text-[var(--magenta)] underline">Rulta</a> og <a href="https://branditscan.com" target="_blank" rel="noopener noreferrer" className="text-[var(--magenta)] underline">BranditScan</a> tilbyr abonnement-basert overvåkning.
                  </li>
                  <li>
                    • <b className="text-[var(--cream-soft)]">Bing / Yandex / DuckDuckGo</b> — samme prosedyre som Google: <a href="https://www.bing.com/webmaster/tools/content-removal" target="_blank" rel="noopener noreferrer" className="text-[var(--magenta)] underline">Bing DMCA</a>, <a href="https://yandex.com/support/legal/index.html" target="_blank" rel="noopener noreferrer" className="text-[var(--magenta)] underline">Yandex Legal</a>.
                  </li>
                  <li>
                    • <b className="text-[var(--cream-soft)]">Datatilsynet</b> — hvis innholdet viser deg som person, kan du kreve sletting under GDPR «rett til å bli glemt». Meld til <a href="https://www.datatilsynet.no/om-datatilsynet/kontakt-oss/klage/" target="_blank" rel="noopener noreferrer" className="text-[var(--magenta)] underline">datatilsynet.no</a>.
                  </li>
                  <li>
                    • <b className="text-[var(--cream-soft)]">Kripos / lokalt politi</b> — hvis innholdet er publisert som hevn eller uten samtykke, er det straffbart etter <a href="https://lovdata.no/dokument/NL/lov/2005-05-20-28/§267a" target="_blank" rel="noopener noreferrer" className="text-[var(--magenta)] underline">straffeloven §267a</a>. Anmeld på <a href="https://www.politiet.no/tjenester/tips-politiet/tips-nettpatruljen/" target="_blank" rel="noopener noreferrer" className="text-[var(--magenta)] underline">politiet.no</a>.
                  </li>
                  <li>
                    • <b className="text-[var(--cream-soft)]">Wayback Machine</b> — be Internet Archive om å ekskludere URL-en, så vises den ikke i historikk-arkivet: <a href="https://help.archive.org/help/how-do-i-request-to-remove-something-from-archive-org/" target="_blank" rel="noopener noreferrer" className="text-[var(--magenta)] underline">help.archive.org</a>.
                  </li>
                </ul>
              </div>
            </div>
            <button onClick={onDone} className="btn-ghost w-full">Ferdig</button>
          </div>
        )}
      </div>
    </div>
  );
}
